import type { ComponentProps } from 'react';
import React from 'react';
import styled from '@emotion/styled';
import type {
  BorderProps,
  DisplayProps,
  HeightProps,
  MinHeightProps,
  MinWidthProps,
  PositionProps,
} from 'styled-system';
import {
  background,
  border,
  borderRight,
  color,
  display,
  height,
  minHeight,
  minWidth,
  position,
  space,
} from 'styled-system';

import Badge from 'common/components/Badge/CountBadge';
import type { FlexProps } from 'common/components/Flex';
import { Flex } from 'common/components/Flex';
import type { GridProps } from 'common/components/Grid';
import { FlexJustifyContent, Grid } from 'common/components/Grid';
import type { IconProps } from 'common/components/Icon';
import Icon from 'common/components/Icon';
import type { CommonTypographyProps } from 'common/components/Typography';
import { SubtitleST4 } from 'common/components/Typography';
import { useLocalization } from 'common/providers/LocaleProvider';
import { useRouter } from 'common/providers/RouterProvider';
import {
  focusOutlineCSSWithOffsetAndRadius,
  hoverOutlineCSSWithOffsetAndRadius,
} from 'common/theme/styles';
import type { ThemeRightProps, ThemeTopProps } from 'common/theme/system-utilities';
import {
  borderRightColor,
  borderRightStyle,
  borderRightWidth,
  cursor,
  textDecoration,
  themeRight,
  themeTop,
} from 'common/theme/system-utilities';
import CartIcon from './CartIcon';
import Logo from './Logo';
import MicroUspHeader from './MicroUspHeader';

interface CheckoutHeaderProps {
  showCart: boolean;
  cartItemsCount: number;
  backToCartUrl: string;
  showCartItemsCount?: boolean;
  withUspHeader?: boolean;
}

const SECURITY_ICON_SIZE = '32px';
const CART_ITEMS_COUNT_POSITION = '-5px';

const StyledGrid = styled(Grid)<GridProps & HeightProps>`
  ${height};
`;
const gridStyles: ComponentProps<typeof StyledGrid> = {
  justifyContent: FlexJustifyContent.SPACE_BETWEEN,
  alignItems: 'center',
  height: ['62px', null, '71px'],
  px: ['sp_16', 'sp_24', 'sp_48', 'sp_64'],
};

const Aside = styled(Flex)`
  ${height};
`;
const asideStyles = {
  height: '100%',
  alignItems: 'center',
};

const SafeArea = styled(Flex)`
  ${height};
  ${space};
  ${borderRightStyle};
  ${borderRightWidth};
  ${borderRightColor};
  ${borderRight};
`;
const safeAreaStyles = {
  height: '40px',
  mr: ['sp_0', 'sp_4'],
  alignItems: 'center',
};

const SafeAreaText = styled(SubtitleST4)<CommonTypographyProps>``;
const safeAreaTextStyles: ComponentProps<typeof SafeAreaText> = {
  color: 'sys.neutral.text.default',
  mr: 'sp_4',
};

const SecurityIcon = styled(Icon)<IconProps & MinWidthProps & MinHeightProps>`
  ${minWidth};
  ${minHeight};
`;
const securityIconStyles: Partial<ComponentProps<typeof SecurityIcon>> = {
  minWidth: SECURITY_ICON_SIZE,
  minHeight: SECURITY_ICON_SIZE,
  size: SECURITY_ICON_SIZE,
  fill: 'sys.neutral.icon.default',
};

const CartLink = styled(Flex)<FlexProps>`
  ${color};
  ${border};
  ${cursor};
  ${background};
  ${textDecoration};
  ${hoverOutlineCSSWithOffsetAndRadius};
  ${focusOutlineCSSWithOffsetAndRadius};
`;
const cartLinkStyles = {
  alignItems: 'center',
  color: 'sys.neutral.border.strong',
  textDecoration: 'none',
  background: 'none',
  border: 'none',
  cursor: 'pointer',
};

const CartLinkText = styled(SubtitleST4)<DisplayProps>`
  ${display};
`;
const cartLinkTextStyles: ComponentProps<typeof CartLinkText> = {
  display: ['none', 'inline-block'],
  mr: 'sp_8',
};

const CartItemsCount = styled(Badge)<PositionProps & ThemeTopProps & ThemeRightProps & BorderProps>`
  ${position};
  ${themeTop};
  ${themeRight};
  ${border};
`;
const cartItemsCountStyles: ComponentProps<typeof CartItemsCount> = {
  position: 'absolute',
  themeTop: CART_ITEMS_COUNT_POSITION,
  themeRight: CART_ITEMS_COUNT_POSITION,
  border: '1px solid white',
};

const CheckoutHeader = ({
  showCart = true,
  backToCartUrl,
  cartItemsCount,
  showCartItemsCount = true,
  withUspHeader = false,
}: CheckoutHeaderProps) => {
  const { r, t } = useLocalization();
  const router = useRouter();

  return (
    <header data-corgi-id="checkout-header">
      <StyledGrid {...gridStyles}>
        <Logo />
        <Aside {...asideStyles}>
          <SafeArea
            {...safeAreaStyles}
            {...(showCart
              ? {
                  borderRightStyle: 'solid',
                  borderRightWidth: 'small',
                  borderRightColor: 'sys.neutral.border.weak',
                  pr: 'sp_4',
                }
              : {})}
          >
            <SafeAreaText {...safeAreaTextStyles}>{t('h24_safe_area')}</SafeAreaText>
            <SecurityIcon name="security" {...securityIconStyles} />
          </SafeArea>
          {showCart && (
            <CartLink
              {...cartLinkStyles}
              as="button"
              onClick={() => router.push(backToCartUrl, { withoutNavigationState: true })}
              title={t('h24_back_to_cart')}
              aria-label={`${t('h24_back_to_cart')}, ${r.t('h24_cart_has_items_count', {
                itemsCount: cartItemsCount,
              })}`}
            >
              <CartLinkText as="span" {...cartLinkTextStyles}>
                {t('h24_back_to_cart')}
              </CartLinkText>
              <Flex as="span" position="relative">
                <CartIcon />
                {showCartItemsCount && (
                  <CartItemsCount {...cartItemsCountStyles}>{cartItemsCount}</CartItemsCount>
                )}
              </Flex>
            </CartLink>
          )}
        </Aside>
      </StyledGrid>
      {withUspHeader && <MicroUspHeader />}
    </header>
  );
};

export default CheckoutHeader;
